import { useCallback, useEffect, useState } from "react";

export interface UseOnlineResult {
  online: boolean;
  /** Call when a fetch fails without ever getting an HTTP response. */
  reportFailure: () => void;
  /** Call when a fetch succeeds, to clear a failure reported earlier. */
  reportSuccess: () => void;
}

/**
 * `navigator.onLine` alone is nearly meaningless as a positive signal — it's
 * `true` on a captive portal, and `true` with no route to the internet at
 * all. It's a trustworthy *negative* signal, though, so this OR's it with
 * whatever the caller has actually observed: a real fetch that never got a
 * response. Cleared by the next reported success, or by the browser's own
 * `online` event.
 */
export function useOnline(): UseOnlineResult {
  const [offline, setOffline] = useState(
    () => typeof navigator !== "undefined" && navigator.onLine === false,
  );

  useEffect(() => {
    const handleOffline = () => setOffline(true);
    const handleOnline = () => setOffline(false);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  const reportFailure = useCallback(() => setOffline(true), []);
  const reportSuccess = useCallback(() => setOffline(false), []);

  return { online: !offline, reportFailure, reportSuccess };
}
